/**
 * PHASE 8 — head-up display geometry.
 *
 * Pure functions deriving the device's geographic heading and tilt from the
 * camera quaternion (world(ENU) -> device), plus the layout of a compass tape
 * rendered along the bottom of the screen.
 *
 * Heading convention: the azimuth (clockwise from north) toward which the
 * device's TOP (+y device axis) points, in the world horizontal plane.
 */

import type { Quat } from "../orientation/quaternion";
import { rotationFromQuat } from "../render/mat4";
import { transformDir, transpose33 } from "../render/skyFrame";

/**
 * Unit world(ENU) direction the device's +y axis points at.
 * qCamera maps world -> device, so apply the transpose (device -> world).
 */
export function deviceTopWorldDirection(q: Quat): readonly [number, number, number] {
  const worldFromDevice = transpose33(rotationFromQuat(q));
  return transformDir(worldFromDevice, [0, 1, 0]);
}

/** Geographic bearing in degrees [0, 360) of the device's top. */
export function deviceHeadingDeg(q: Quat): number {
  const d = deviceTopWorldDirection(q);
  // Compass convention: 0 = north, 90 = east, 180 = south, 270 = west.
  // ENU d = (x east, y north) -> bearing = atan2(east, north).
  const bearing = (Math.atan2(d[0], d[1]) * 180) / Math.PI;
  return (bearing + 360) % 360;
}

/** Elevation in degrees (-90..+90) of the device's top (0 = level). */
export function deviceTiltDeg(q: Quat): number {
  const d = deviceTopWorldDirection(q);
  return (Math.asin(Math.min(1, Math.max(-1, d[2]))) * 180) / Math.PI;
}

const CARDINALS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;

export interface CompassTick {
  /** Bearing this tick marks (degrees, 0..360). */
  bearing: number;
  /** True for the 16 "hour" ticks, false for the 8 cardinal+intercardinal names. */
  major: boolean;
  /** Cardinal label (N, NE, ...) for the 8 named ticks, else null. */
  label: string | null;
  /** Horizontal pixel offset from the tape centre (right = +). */
  offsetPx: number;
}

/**
 * Layout of a compass tape centred on the device heading: named cardinal
 * ticks every 45°, minor ticks every 22.5°. Returns ticks in screen order
 * (left to right) with pixel offsets relative to the tape's centre.
 */
export function compassTape(headingDeg: number, widthPx: number, tickStepDeg = 22.5): CompassTick[] {
  const pxPerDeg = widthPx / 180; // 90 degrees visible either side of the heading
  const ticks: CompassTick[] = [];
  const startBearing = headingDeg - 90;
  const endBearing = headingDeg + 90;
  for (let b = Math.ceil(startBearing / tickStepDeg) * tickStepDeg; b <= endBearing; b += tickStepDeg) {
    const bearing = ((b % 360) + 360) % 360;
    const major = Math.abs(bearing % 45) < 1e-9;
    ticks.push({
      bearing,
      major,
      label: major ? CARDINALS[Math.round(bearing / 45) % 8]! : null,
      offsetPx: (b - headingDeg) * pxPerDeg,
    });
  }
  return ticks;
}

/** Whether a device-relative heading reading is trustworthy enough to display. */
export function headingUsable(tiltDeg: number): boolean {
  // Past ~70° of tilt the horizontal component of the device top vanishes;
  // bearings flip and jitter there.
  return Math.abs(tiltDeg) <= 70;
}